import { computed, getCurrentInstance, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useForm } from 'vee-validate';
import type { ZodSchema } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useDebounceFn, watchDebounced } from '@vueuse/core';
import { useElementProperties } from './useElementProperties';
import { useFormBuilderStore } from './form-builder.store';
import type { FormElement } from './form.types';

interface UsePropertyFormOptions<TValues extends object, TProperties> {
  elementId?: (() => string | undefined) | string;
  validationSchema: ZodSchema<TValues>;
  extractInitialValues: (properties: TProperties | undefined) => TValues;
  mapValuesToProperties: (values: TValues, currentProperties: TProperties) => Partial<FormElement>;
  debounceMs?: number;
  validationDebounceMs?: number;
}

export function usePropertyForm<TValues extends object, TProperties = unknown>(
  options: UsePropertyFormOptions<TValues, TProperties>
) {
  const {
    elementId,
    validationSchema,
    extractInitialValues,
    mapValuesToProperties,
    debounceMs = 300,
    validationDebounceMs = 1000,
  } = options;

  const { selectedElement, updateElement, cellPosition } = useElementProperties(elementId);
  const hasTrackedHistoryForCurrentElement = ref(false);
  const formBuilderStore = useFormBuilderStore();
  const editorId = useId();

  const formSchema = toTypedSchema(validationSchema);

  const initialValues = computed(() => {
    const props = selectedElement.value?.properties as TProperties | undefined;
    return extractInitialValues(props);
  });

  const { values, setValues, errors, meta, validate } = useForm<TValues>({
    validationSchema: formSchema,
    initialValues: {} as never,
  });

  watch(
    () => selectedElement.value?.id,
    (newId, oldId) => {
      hasTrackedHistoryForCurrentElement.value = false;

      if (newId && newId !== oldId) {
        setValues(initialValues.value as never);
        validate();
      }
    },
    { immediate: true }
  );

  watch(
    () => meta.value.valid,
    (valid) => formBuilderStore.setPropertyEditorValidity(editorId, valid),
    { immediate: true }
  );

  function performCommit() {
    if (!selectedElement.value) return;

    updateElement(
      mapValuesToProperties(values as TValues, selectedElement.value.properties as TProperties),
      { trackHistory: !hasTrackedHistoryForCurrentElement.value }
    );
    hasTrackedHistoryForCurrentElement.value = true;
  }

  async function commitProperties() {
    if (!selectedElement.value) return;

    // Validate before updating to prevent saving invalid data
    const validationResult = await validate();
    if (!validationResult.valid) return;

    performCommit();
  }

  const updateProperties = useDebounceFn(commitProperties, debounceMs);

  watch(values, updateProperties, { deep: true });

  // Flush any pending edit before the component unmounts. Deselecting an element
  // (outside click / Escape) sets selection to null and the sidebar re-keys, which
  // unmounts this component and cancels the debounced write. selectedElement is
  // resolved from the elementId prop (still valid during teardown), so a synchronous
  // commit succeeds and prevents losing the last change. A synchronous safeParse is
  // used instead of the async validate() because unmount hooks cannot await.
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      formBuilderStore.clearPropertyEditorValidity(editorId);

      if (!validationSchema.safeParse(values).success) return;

      performCommit();
    });
  }

  // Debounced validation for showing errors - wait for user to stop typing
  // This applies to all fields including minLength and maxLength
  watchDebounced(
    values,
    () => {
      validate();
    },
    { debounce: validationDebounceMs, deep: true }
  );

  return {
    selectedElement,
    cellPosition,
    values,
    setValues,
    errors,
    meta,
    updateElement,
  };
}
